import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';

import { useProjects } from '../hooks/use-projects';
import { ProjectGeneralTab } from './ProjectGeneralTab';
import { ProjectMembersTab } from './ProjectMembersTab';

interface ProjectSettingDialogProps {
  projectId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type TabType = 'general' | 'members';

export const ProjectSettingDialog = ({
  projectId,
  open,
  onOpenChange,
}: ProjectSettingDialogProps) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<TabType>('general');
  const { getCurrentUserRole, getProject } = useProjects();

  const project = getProject(projectId);
  const currentUserRole = getCurrentUserRole(projectId);
  const isOwner = currentUserRole === 'OWNER';

  const tabs = [
    { id: 'general' as const, label: t('modules.mavue2.settings.categories.general') },
    { id: 'members' as const, label: t('modules.mavue2.settings.categories.members') },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl h-[80vh] p-0 flex flex-col overflow-hidden">
        {/* Header */}
        <DialogHeader className="px-6 py-4 border-b flex-shrink-0">
          <DialogTitle className="text-xl font-semibold">{project?.title}</DialogTitle>
        </DialogHeader>

        {/* Tabs */}
        <div className="px-6 pt-4 flex-shrink-0">
          <div className="flex space-x-8 border-b">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`pb-3 px-1 text-sm font-medium transition-colors relative ${
                  activeTab === tab.id ? 'text-gray-900' : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {tab.label}
                {activeTab === tab.id && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600 rounded-full" />
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Scrollable content with shadcn ScrollArea */}
        <ScrollArea className="flex-1 px-2 pb-6">
          {activeTab === 'general' && (
            <ProjectGeneralTab
              projectId={projectId}
              isOwner={isOwner}
              onClose={() => onOpenChange(false)}
            />
          )}
          {activeTab === 'members' && <ProjectMembersTab projectId={projectId} isOwner={isOwner} />}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};
